import { type FormEvent, useState } from 'react';
import { deleteAccount, ServerError, signOut } from '../account';
import { type MessageKey, useT } from '../i18n';
import { useGame } from '../store';
import { AuthPanel } from './AuthPanel';
import { Icon } from './icons';

/**
 * The account on the title screen. Playing needs none: without one the games are kept on this
 * device. An account keeps them on the server, so the same airport goes on from a phone, a
 * tablet or a computer (and this device's games move to it on signing in). Co-op, the
 * leaderboard and the profile need one. Signed in: who, sign out, and deleting the account.
 */
export function AccountPage({
  reason,
  onSignedIn,
}: {
  /** What sent the player here: a feature that needs an account (its menu key), or none. */
  reason: 'coop' | 'leaderboard' | 'profile' | null;
  onSignedIn: () => void;
}) {
  const t = useT();
  const account = useGame((s) => s.account);
  const [moved, setMoved] = useState<number | null>(null);

  if (account) {
    return (
      <div className="account-page">
        <div className="sync-card on">
          <Devices />
          <p>
            <strong>{t('account.signedInAs', { name: account.username })}</strong>
            <br />
            {t('account.syncOn')}
          </p>
        </div>
        {moved !== null && moved > 0 && (
          <div className="banner ok small" role="status">
            <Icon name="check" size={16} />
            {t('account.moved', { n: moved })}
          </div>
        )}
        <button type="button" className="btn ghost" onClick={signOut}>
          <Icon name="exit" size={16} />
          {t('auth.logout')}
        </button>
        <DeleteAccount />
      </div>
    );
  }

  return (
    <div className="account-page">
      {reason && (
        <div className="banner info small" role="status">
          <Icon name="lock" size={16} />
          {t('account.needed', { what: t(`menu.${reason}` as MessageKey) })}
        </div>
      )}
      <div className="sync-card">
        <Devices />
        <p>
          <strong>{t('account.local')}</strong>
          <br />
          {t('account.why')}
        </p>
      </div>
      <AuthPanel
        onSignedIn={(n) => {
          setMoved(n);
          onSignedIn();
        }}
      />
    </div>
  );
}

/** A phone, a tablet and a computer, joined by the cloud: one game on all three. */
function Devices() {
  return (
    <div className="sync-devices" aria-hidden>
      <Icon name="phone" size={26} />
      <Icon name="tablet" size={30} />
      <Icon name="monitor" size={34} />
      <Icon name="cloud" size={22} className="sync-cloud" />
    </div>
  );
}

/** Deleting the account for good: its saves and profile with it, the password asked again. */
function DeleteAccount() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount(password);
    } catch (err) {
      setError(err instanceof ServerError ? err.code : 'server');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="link-btn danger-link" onClick={() => setOpen(true)}>
        {t('account.delete')}
      </button>
    );
  }
  return (
    <form className="delete-account" onSubmit={(e) => void submit(e)}>
      <div className="banner error small" role="alert">
        <Icon name="warning" size={16} />
        {t('account.deleteWarn')}
      </div>
      <label className="field">
        <span>{t('auth.password')}</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          maxLength={128}
          required
        />
      </label>
      {error && (
        <p className="error-text small">
          {t(error === 'wrongPassword' ? 'account.wrongPassword' : 'auth.error.server')}
        </p>
      )}
      <div className="row-actions">
        <button type="button" className="btn ghost" onClick={() => setOpen(false)}>
          {t('account.keep')}
        </button>
        <button type="submit" className="btn danger" disabled={busy || !password}>
          <Icon name="trash" size={16} />
          {t(busy ? 'auth.wait' : 'account.deleteNow')}
        </button>
      </div>
    </form>
  );
}
